import { useParams, Link } from "react-router-dom";
import { Seo, localBusinessLD, breadcrumbLD } from "../lib/seo";
import { cityBySlug, CITIES } from "../data/cities";
import { SERVICES, SITE } from "../data/site";
import { Reveal } from "../components/Reveal";
import { PageHero, CtaBand } from "../components/Shared";
import NotFound from "./NotFound";

export default function CityPage() {
    const { slug } = useParams();
    const city = cityBySlug(slug.replace(/^peintre-/, ""));
    if (!city) return <NotFound />;

    const near = CITIES.filter((c) => c.slug !== slug.replace(/^peintre-/, "")).slice(0, 5);
    const title = `Peintre & rénovation intérieure à ${city.name} (${city.cp}) — Solino Pro`;

    return (
        <>
            <Seo
                title={title}
                description={`Rénovation intérieure à ${city.name} (${city.cp}) : peinture, parquet, carrelage, sols et murs. Devis gratuit sous 48 h, artisan local basé à ${SITE.city}, à ${city.km === 0 ? "quelques minutes" : `${city.km} km`}. ${city.tagline}`}
                path={`/peintre-${city.slug}`}
                jsonld={[
                    localBusinessLD(),
                    breadcrumbLD([
                        { name: "Accueil", path: "/" },
                        { name: `Peintre ${city.name}`, path: `/peintre-${city.slug}` },
                    ]),
                ]}
            />
            <PageHero
                img={city.hero}
                crumb={`Solino Pro — ${city.dept}`}
                title={`Peintre & sols à ${city.name},`}
                serif="vos voisins."
            />

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-[1.5fr_1fr] gap-14" data-testid="city-content">
                <Reveal>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold mb-5">{city.tagline}</p>
                    <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-ivory leading-tight">
                        Un artisan de {SITE.city},
                        <span className="font-display italic text-gold"> pas une plateforme.</span>
                    </h2>
                    <p className="mt-8 text-base sm:text-lg text-smoke leading-relaxed">{city.intro}</p>
                    <p className="mt-6 text-base text-smoke leading-relaxed">{city.body}</p>
                    <p className="mt-6 text-sm text-smoke leading-relaxed border-l-2 border-gold/40 pl-5" data-testid="city-seo-line">
                        Rénovation intérieure à {city.name} — sols et murs, peinture, parquet stratifié, carrelage :
                        Solino Pro prend en charge votre projet de A à Z, {city.km === 0 ? "depuis notre atelier communal" : `depuis notre atelier de ${SITE.city} (${city.km} km)`}.
                    </p>
                    <p className="mt-6 border-l-2 border-gold pl-5 text-sm text-ivory/75 leading-relaxed">{city.local}</p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link to="/devis" className="inline-flex items-center rounded-full bg-gold text-ink font-bold px-8 py-4 hover:scale-[1.04] transition-transform duration-300" data-testid="city-cta-devis">
                            Devis gratuit à {city.name}
                        </Link>
                        <a href={SITE.phoneHref} className="inline-flex items-center rounded-full border border-gold/60 text-gold px-8 py-4 hover:bg-gold/10 transition-colors" data-testid="city-cta-call">
                            {SITE.phoneDisplay}
                        </a>
                    </div>
                </Reveal>

                <Reveal delay={0.12}>
                    <aside className="border border-linew bg-coal p-8 sticky top-28" data-testid="city-services">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold mb-6">Nos services à {city.name}</p>
                        <ul className="space-y-4">
                            {SERVICES.map((s) => (
                                <li key={s.slug}>
                                    <Link to={`/${s.slug}`} className="group flex items-center justify-between gap-3 text-sm text-ivory/80 hover:text-gold transition-colors" data-testid={`city-service-${s.slug}`}>
                                        {s.title}
                                        <span className="text-gold opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        <div className="gold-rule my-7" />
                        <p className="text-xs text-smoke leading-relaxed">
                            {city.km === 0
                                ? `Nous sommes basés à ${city.name} : déplacement gratuit et visite rapide.`
                                : `À ${city.km} km de notre atelier : déplacement gratuit, devis sous 48 h.`}
                        </p>
                    </aside>
                </Reveal>
            </section>

            <section className="border-t border-linew bg-coal/60" data-testid="city-nearby">
                <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold mb-8">Communes proches</p>
                    <div className="flex flex-wrap gap-3">
                        {near.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/peintre-${c.slug}`}
                                className="rounded-full border border-linew px-5 py-2.5 text-sm text-ivory/75 hover:border-gold hover:text-gold transition-colors"
                                data-testid={`city-near-${c.slug}`}
                            >
                                {c.name}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand />
        </>
    );
}
